import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, InputAdornment, Popper, TextField, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { matchKorean, getChosung } from '../../utils/methods';

// ㄱㄴㄷ → 영어 → 숫자 → 특문 (판매등록 수강권 패널과 동일 정렬)
const charOrder = c => {
  if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(c)) return 0;
  if (/[a-zA-Z]/.test(c)) return 1;
  if (/[0-9]/.test(c)) return 2;
  return 3;
};

/**
 * 담당 강사 검색형 다중 선택. 판매등록 "수강권 선택" 패널과 같은 구성이다.
 * 값 계약은 FormInputMultiDropdown 과 동일 — option.value 배열이며 선택 순서를 보존한다
 * (첫 번째가 구서버 호환 TRAINER_ID 로 쓰인다).
 */
export const TrainerSearchSelect = ({
  control,
  name = 'trainers',
  options = [],
  placeholder = '담당 강사를 선택해주세요',
  searchPlaceholder = '강사 검색',
  // true 면 목록 패널이 아래 요소를 밀지 않고 위에 떠서 열린다(바깥 클릭으로 닫힘)
  overlay = false,
  // 패널이 열려 있는 동안 패널 왼쪽 옆에 띄우는 보조 영역(판매 담당자 비율 편집). overlay 일 때만 쓴다.
  sidePanel = null,
  // 잠금(판매정보 수정 읽기전용) — 헤더를 회색으로 두고 펼치지 않는다
  disabled = false,
  // true 면 목록 맨 위에 '전체' 행을 둔다(전원 선택/해제). 검색 중에는 숨긴다
  selectAll = false,
  sx,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  // overlay 패널은 Popper(body 포탈)에 그린다 — 모달의 overflow 에 잘리지 않게. 바깥 클릭 판정에 포함해야 한다.
  const popperRef = useRef(null);

  useEffect(() => {
    if (!overlay || !open) return undefined;
    const onMouseDown = e => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPopper = popperRef.current && popperRef.current.contains(e.target);
      if (!inRoot && !inPopper) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [overlay, open]);
  const [keyword, setKeyword] = useState('');

  // 옵션 첫 행의 안내용 빈 값은 목록에서 제외한다
  const trainerOptions = useMemo(
    () => (options || []).filter(option => option.value !== '' && option.value != null),
    [options],
  );

  const filteredTrainers = useMemo(() => {
    const word = keyword.trim();
    const filtered = word
      ? trainerOptions.filter(option => matchKorean(option.label || '', word))
      : [...trainerOptions];
    filtered.sort((a, b) => {
      const orderDiff = charOrder((a.label || '')[0] || '') - charOrder((b.label || '')[0] || '');
      if (orderDiff !== 0) return orderDiff;
      return (a.label || '').localeCompare(b.label || '', 'ko');
    });
    return filtered;
  }, [trainerOptions, keyword]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const selected = Array.isArray(value) ? value : [];
        const selectedLabels = selected
          .map(id => trainerOptions.find(option => option.value === id)?.label)
          .filter(Boolean)
          .join(', ');

        const toggle = id => {
          // 선택 순서 보존 — 새로 고른 강사는 뒤에 붙인다
          onChange(selected.includes(id) ? selected.filter(selectedId => selectedId !== id) : [...selected, id]);
        };

        const allSelected =
          trainerOptions.length > 0 && trainerOptions.every(option => selected.includes(option.value));
        // 전체 선택 시 기존 선택 순서를 앞에 두고 나머지를 목록 순서대로 붙인다
        const toggleAll = () => {
          onChange(
            allSelected
              ? []
              : [
                  ...selected,
                  ...filteredTrainers.map(option => option.value).filter(id => !selected.includes(id)),
                ],
          );
        };

        const selectAllRow = selectAll && !keyword.trim() && trainerOptions.length > 0 && (
          <Box
            onClick={toggleAll}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              px: '14px',
              py: '10px',
              cursor: 'pointer',
              backgroundColor: allSelected ? '#f0f8f8' : '#fff',
              borderBottom: '1px solid #eee',
              '&:hover': { backgroundColor: allSelected ? '#e6f5f5' : '#f5f5f5' },
            }}>
            {allSelected && <CheckCircleIcon sx={{ fontSize: 18, color: '#4AB3BC', flexShrink: 0 }} />}
            <Typography sx={{ fontSize: '13px', color: '#333', fontWeight: allSelected ? 600 : 400 }}>
              전체
            </Typography>
          </Box>
        );

        return (
          <Box ref={rootRef} sx={{ width: '100%', position: 'relative', ...sx }}>
            {/* 헤더 (클릭하면 펼침/접힘) */}
            <Box
              onClick={() => {
                if (disabled) return;
                setOpen(prev => !prev);
                setKeyword('');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '44px',
                px: '14px',
                border: open ? '1.5px solid #4AB3BC' : '1px solid #c4c4c4',
                borderRadius: open ? '6px 6px 0 0' : '6px',
                cursor: disabled ? 'default' : 'pointer',
                backgroundColor: disabled ? '#f5f5f5' : selected.length ? '#f0f8f8' : '#fff',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: disabled ? '#c4c4c4' : '#4AB3BC' },
              }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: selected.length ? 500 : 400,
                  color: disabled ? '#737373' : selected.length ? '#4AB3BC' : '#999',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {selectedLabels || placeholder}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: open ? '#4AB3BC' : 'transparent',
                  transition: 'all 0.15s ease',
                }}>
                {open ? (
                  <KeyboardArrowUpIcon sx={{ color: '#fff', fontSize: 18 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
                )}
              </Box>
            </Box>

            {/* 패널 (펼쳐졌을 때). overlay 면 Popper 로 띄워 아래 요소를 밀지도, 모달 overflow 에 잘리지도 않는다 */}
            {open &&
              (overlay ? (
                <Popper
                  open
                  anchorEl={rootRef.current}
                  placement="bottom-start"
                  style={{ zIndex: 1500, width: rootRef.current ? rootRef.current.offsetWidth : undefined }}>
                  <Box ref={popperRef} sx={{ position: 'relative' }}>
                    {/* 패널 옆 보조 영역(판매 담당자 비율) — 패널과 같이 열리고 닫힌다 */}
                    {sidePanel && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 'calc(100% + 12px)',
                          width: '240px',
                          backgroundColor: '#fff',
                          border: '1.5px solid #4AB3BC',
                          borderRadius: '6px',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                        }}>
                        {sidePanel}
                      </Box>
                    )}

                    <Box
                      sx={{
                        border: '1.5px solid #4AB3BC',
                        borderTop: 'none',
                        borderRadius: '0 0 6px 6px',
                        maxHeight: '320px',
                        ...(overlay && {
                          backgroundColor: '#fff',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                        }),
                        display: 'flex',
                        flexDirection: 'column',
                      }}>
                      <Box sx={{ p: '10px 12px', borderBottom: '1px solid #eee' }}>
                        <TextField
                          size="small"
                          placeholder={searchPlaceholder}
                          value={keyword}
                          onChange={e => setKeyword(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                          fullWidth
                          sx={{ '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      <Box sx={{ overflowY: 'auto', flex: 1 }}>
                        {selectAllRow}
                        {filteredTrainers.length === 0 ? (
                          <Box sx={{ p: '24px', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '13px', color: '#999' }}>검색 결과가 없습니다</Typography>
                          </Box>
                        ) : (
                          (() => {
                            let lastCho = '';
                            return filteredTrainers.map(option => {
                              const cho = getChosung((option.label || '')[0] || '');
                              const showHeader = cho !== lastCho;
                              lastCho = cho;
                              const isSelected = selected.includes(option.value);
                              return (
                                <React.Fragment key={option.value}>
                                  {showHeader && (
                                    <Box
                                      sx={{
                                        px: '14px',
                                        py: '4px',
                                        backgroundColor: '#f7f7f7',
                                        borderBottom: '1px solid #eee',
                                      }}>
                                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#999' }}>
                                        {cho}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box
                                    onClick={() => toggle(option.value)}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      px: '14px',
                                      py: '10px',
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? '#f0f8f8' : '#fff',
                                      borderBottom: '1px solid #f5f5f5',
                                      '&:hover': { backgroundColor: isSelected ? '#e6f5f5' : '#f5f5f5' },
                                      '&:last-child': { borderBottom: 'none' },
                                    }}>
                                    {isSelected && (
                                      <CheckCircleIcon sx={{ fontSize: 18, color: '#4AB3BC', flexShrink: 0 }} />
                                    )}
                                    <Typography
                                      sx={{
                                        fontSize: '13px',
                                        color: '#333',
                                        fontWeight: isSelected ? 600 : 400,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}>
                                      {option.label}
                                    </Typography>
                                  </Box>
                                </React.Fragment>
                              );
                            });
                          })()
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Popper>
              ) : (
                <Box
                  sx={{
                    border: '1.5px solid #4AB3BC',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    maxHeight: '320px',
                    ...(overlay && {
                      backgroundColor: '#fff',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    }),
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                  <Box sx={{ p: '10px 12px', borderBottom: '1px solid #eee' }}>
                    <TextField
                      size="small"
                      placeholder={searchPlaceholder}
                      value={keyword}
                      onChange={e => setKeyword(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      autoFocus
                      fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box sx={{ overflowY: 'auto', flex: 1 }}>
                    {selectAllRow}
                    {filteredTrainers.length === 0 ? (
                      <Box sx={{ p: '24px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', color: '#999' }}>검색 결과가 없습니다</Typography>
                      </Box>
                    ) : (
                      (() => {
                        let lastCho = '';
                        return filteredTrainers.map(option => {
                          const cho = getChosung((option.label || '')[0] || '');
                          const showHeader = cho !== lastCho;
                          lastCho = cho;
                          const isSelected = selected.includes(option.value);
                          return (
                            <React.Fragment key={option.value}>
                              {showHeader && (
                                <Box
                                  sx={{
                                    px: '14px',
                                    py: '4px',
                                    backgroundColor: '#f7f7f7',
                                    borderBottom: '1px solid #eee',
                                  }}>
                                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#999' }}>
                                    {cho}
                                  </Typography>
                                </Box>
                              )}
                              <Box
                                onClick={() => toggle(option.value)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  px: '14px',
                                  py: '10px',
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#f0f8f8' : '#fff',
                                  borderBottom: '1px solid #f5f5f5',
                                  '&:hover': { backgroundColor: isSelected ? '#e6f5f5' : '#f5f5f5' },
                                  '&:last-child': { borderBottom: 'none' },
                                }}>
                                {isSelected && (
                                  <CheckCircleIcon sx={{ fontSize: 18, color: '#4AB3BC', flexShrink: 0 }} />
                                )}
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    color: '#333',
                                    fontWeight: isSelected ? 600 : 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                  {option.label}
                                </Typography>
                              </Box>
                            </React.Fragment>
                          );
                        });
                      })()
                    )}
                  </Box>
                </Box>
              ))}
          </Box>
        );
      }}
    />
  );
};

export default TrainerSearchSelect;
